import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import { getDb } from '../firebase';

export const createChore = async (familyId, choreData) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'chores'));
  const data = {
    ...choreData,
    isActive: true,
    createdAt: Timestamp.now()
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

export const updateChore = async (familyId, choreId, updates) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'chores', choreId), updates);
};

export const deleteChore = async (familyId, choreId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'chores', choreId));
};

export const subscribeToChores = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'chores'),
    where('isActive', '==', true)
  );
  return onSnapshot(q, (snapshot) => {
    const chores = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(chores);
  }, (error) => {
    console.error('Chores listener error:', error);
  });
};

export const completeChore = async (familyId, choreId, completionData) => {
  const db = getDb();
  const ref = doc(collection(db, 'families', familyId, 'chores', choreId, 'completions'));
  const data = {
    ...completionData,
    completedAt: Timestamp.now()
  };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

export const uncompleteChore = async (familyId, choreId, completionId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'chores', choreId, 'completions', completionId));
};

export const subscribeToChoreCompletions = (familyId, choreId, dateStr, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'chores', choreId, 'completions'),
    where('date', '==', dateStr)
  );
  return onSnapshot(q, (snapshot) => {
    const completions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(completions);
  }, (error) => {
    console.error('Chore completions listener error:', error);
  });
};

export const subscribeToDailyCompletions = (familyId, dateStr, callback) => {
  const db = getDb();

  // First subscribe to the family's chores, then subscribe to each chore's completions
  const choreUnsubs = [];
  const completionsByChore = {};

  const emitAll = () => {
    const all = Object.values(completionsByChore).flat();
    callback(all);
  };

  const choresUnsub = onSnapshot(
    query(collection(db, 'families', familyId, 'chores'), where('isActive', '==', true)),
    (choreSnap) => {
      const currentChoreIds = new Set(choreSnap.docs.map(d => d.id));

      // Remove subs for deleted chores
      Object.keys(completionsByChore).forEach(cid => {
        if (!currentChoreIds.has(cid)) {
          delete completionsByChore[cid];
        }
      });

      // Add subs for new chores
      choreSnap.docs.forEach(choreDoc => {
        const choreId = choreDoc.id;
        if (completionsByChore[choreId] !== undefined) return; // already subscribed
        completionsByChore[choreId] = [];

        const cq = query(
          collection(db, 'families', familyId, 'chores', choreId, 'completions'),
          where('date', '==', dateStr)
        );
        const unsub = onSnapshot(cq, (compSnap) => {
          completionsByChore[choreId] = compSnap.docs.map(d => ({
            id: d.id,
            choreId,
            ...d.data()
          }));
          emitAll();
        }, (error) => {
          console.error(`Completions listener error for chore ${choreId}:`, error);
        });
        choreUnsubs.push(unsub);
      });

      emitAll();
    },
    (error) => {
      console.error('Chores listener (completions) error:', error);
    }
  );

  return () => {
    choresUnsub();
    choreUnsubs.forEach(u => u());
  };
};
